import { useCallback, useEffect, useRef, useState } from 'react';

import { enfileirarRascunho, rascunhoNaFila, semRede, tirarDaFila } from '@/comuns/filaOffline';
import { dados } from '@/data/porta';
import type { RespostaDaFicha } from '@/data/tipos';

export type EstadoDoRascunho =
    | { tipo: 'parado' }
    | { tipo: 'salvando' }
    | { tipo: 'salvo'; em: string }
    | { tipo: 'recuperado'; em: string }
    | { tipo: 'na-fila' }
    | { tipo: 'erro'; mensagem: string };

type Respostas = Record<string, RespostaDaFicha>;

/** O mesmo intervalo do protótipo: guarda depois da última tecla, não a cada uma. */
const ESPERA_MS = 800;

function hora(iso: string): string {
    return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

export function textoDoRascunho(estado: EstadoDoRascunho): string {
    switch (estado.tipo) {
        case 'salvando': return 'Salvando rascunho…';
        case 'salvo': return `Rascunho salvo às ${hora(estado.em)}`;
        case 'recuperado': return `Rascunho de ${hora(estado.em)} recuperado`;
        case 'na-fila': return 'Sem conexão — rascunho guardado neste navegador até a rede voltar';
        case 'erro': return estado.mensagem;
        default: return 'O rascunho se guarda sozinho enquanto você escreve';
    }
}

/**
 * O rascunho da ficha enquanto a consulta está aberta — trilha T5a.
 *
 * Sem botão: guarda um instante depois da última tecla. Sem rede, vai para a
 * fila do navegador; com rede, erro aparece. `alvo` nulo desliga tudo — é o
 * caso do prontuário fora da consulta e da preferência desligada.
 */
export function useRascunhoDaFicha(
    alvo: { atendimentoId: string; modeloId: string } | null,
    aberto: boolean,
    aoRecuperar: (respostas: Respostas) => void,
) {
    const [estado, setEstado] = useState<EstadoDoRascunho>({ tipo: 'parado' });
    const espera = useRef<number | null>(null);
    const pendente = useRef<Respostas | null>(null);
    const recuperar = useRef(aoRecuperar);
    useEffect(() => { recuperar.current = aoRecuperar; });

    const atendimentoId = alvo?.atendimentoId;
    const modeloId = alvo?.modeloId;

    const gravarAgora = useCallback(async (respostas: Respostas) => {
        if (!atendimentoId || !modeloId) return;
        pendente.current = null;
        if (semRede()) {
            setEstado(enfileirarRascunho(atendimentoId, modeloId, respostas)
                ? { tipo: 'na-fila' }
                : { tipo: 'erro', mensagem: 'Sem conexão, e este navegador não deixou guardar o rascunho.' });
            return;
        }
        setEstado({ tipo: 'salvando' });
        try {
            const salvo = await dados().salvarRascunhoDaFicha(atendimentoId, modeloId, respostas);
            tirarDaFila(atendimentoId, modeloId);
            setEstado({ tipo: 'salvo', em: salvo.atualizadoEm });
        } catch (e) {
            setEstado({ tipo: 'erro', mensagem: e instanceof Error ? e.message : 'Não foi possível salvar o rascunho.' });
        }
    }, [atendimentoId, modeloId]);

    useEffect(() => {
        if (!aberto || !atendimentoId || !modeloId) return;
        let vivo = true;
        setEstado({ tipo: 'parado' });
        const daFila = rascunhoNaFila(atendimentoId, modeloId);
        dados().rascunhoDaFicha(atendimentoId, modeloId)
            .then((doServidor) => {
                if (!vivo) return;
                /* O mais novo vence: o que foi escrito sem rede é mais recente
                   que o que o servidor guardou antes de a rede cair. */
                const escolhido = daFila && (!doServidor || daFila.guardadoEm > doServidor.atualizadoEm)
                    ? { respostas: daFila.respostas, em: daFila.guardadoEm }
                    : doServidor ? { respostas: doServidor.respostas, em: doServidor.atualizadoEm } : null;
                if (escolhido && Object.keys(escolhido.respostas).length > 0) {
                    recuperar.current(escolhido.respostas);
                    setEstado({ tipo: 'recuperado', em: escolhido.em });
                }
            })
            .catch((e: unknown) => {
                if (!vivo) return;
                if (daFila) {
                    recuperar.current(daFila.respostas);
                    setEstado({ tipo: 'recuperado', em: daFila.guardadoEm });
                } else {
                    setEstado({ tipo: 'erro', mensagem: e instanceof Error ? e.message : 'Não foi possível ler o rascunho.' });
                }
            });
        return () => { vivo = false; };
    }, [aberto, atendimentoId, modeloId]);

    const agendar = useCallback((respostas: Respostas) => {
        if (!atendimentoId || !modeloId) return;
        pendente.current = respostas;
        if (espera.current !== null) window.clearTimeout(espera.current);
        espera.current = window.setTimeout(() => {
            espera.current = null;
            void gravarAgora(respostas);
        }, ESPERA_MS);
    }, [atendimentoId, modeloId, gravarAgora]);

    /** Fechar logo depois de digitar não perde a última tecla: grava na hora. */
    const gravarPendente = useCallback(() => {
        if (espera.current !== null) {
            window.clearTimeout(espera.current);
            espera.current = null;
        }
        if (pendente.current) void gravarAgora(pendente.current);
    }, [gravarAgora]);

    /** A ficha foi para o prontuário: o rascunho sai, do servidor e da fila. */
    const descartar = useCallback(async () => {
        if (espera.current !== null) {
            window.clearTimeout(espera.current);
            espera.current = null;
        }
        pendente.current = null;
        if (!atendimentoId || !modeloId) return;
        tirarDaFila(atendimentoId, modeloId);
        setEstado({ tipo: 'parado' });
        await dados().descartarRascunhoDaFicha(atendimentoId, modeloId);
    }, [atendimentoId, modeloId]);

    return { estado, agendar, gravarPendente, descartar };
}
